/* Optic Souq — experience layer.

   Quick view, motion and the grid toggles. Reuses window.os2Add / window.os2Toast
   / window.OS2 from os2.js rather than re-implementing the cart plumbing. Those
   are read at click time, not parse time, so load order between the two files
   does not matter.

   EVENT BINDING: the theme re-renders sections after load (morph.js /
   section-hydration.js), which discards listeners attached to elements. Every
   handler here is delegated from document, and the reveal observer re-scans on
   DOM mutation. Do not convert these to per-element addEventListener.

   Product data for the modal comes from /products/{handle}.js — one small JSON
   request, cached per handle for the life of the page. */
(function () {
  'use strict';

  var CFG = window.OS2 || {};
  var MOQ = CFG.moq || 3;
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function money(paise) {
    if (typeof window.os2Money === 'function') return window.os2Money(paise);
    return '₹' + (Math.round(paise) / 100).toFixed(2);
  }

  /* ------------------------------------------------------------ 1. MOTION */

  var io = null;
  if ('IntersectionObserver' in window && !REDUCED) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-seen');
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .06 });
  }

  function observeReveals(root) {
    var nodes = (root || document).querySelectorAll('.os2x-rise:not(.is-seen)');
    if (!io) {
      Array.prototype.forEach.call(nodes, function (n) { n.classList.add('is-seen'); });
      return;
    }
    Array.prototype.forEach.call(nodes, function (n) { io.observe(n); });
  }

  /* Auto-enrol the grids the store already renders, so sections do not each
     have to remember to add the class. Stagger is capped so a 60-card
     collection page does not end up with a 6-second cascade. */
  function enrol(root) {
    var groups = (root || document).querySelectorAll('[data-os2x-stagger]');
    Array.prototype.forEach.call(groups, function (g) {
      var kids = g.children;
      for (var i = 0; i < kids.length; i++) {
        if (kids[i].classList.contains('os2x-rise')) continue;
        kids[i].classList.add('os2x-rise');
        kids[i].style.setProperty('--os2x-delay', Math.min(i, 8) * 45 + 'ms');
      }
    });
    observeReveals(root);
  }

  /* ------------------------------------------------- 2. QUICK VIEW: markup */

  var EYE =
    '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
    '<path d="M1.7 10S4.6 4.8 10 4.8 18.3 10 18.3 10 15.4 15.2 10 15.2 1.7 10 1.7 10Z" stroke="currentColor" stroke-width="1.5"/>' +
    '<circle cx="10" cy="10" r="2.4" stroke="currentColor" stroke-width="1.5"/></svg>';

  /* Quick view is injected, not templated.

     Every product card in this theme — os2-card, osr-product-card, the home
     shelves, recommendations — is ultimately an <a href="/products/…"> wrapping
     an <img>. Injecting from here means one implementation covers every page,
     including sections we have not touched, instead of editing a dozen Liquid
     snippets and missing the next one somebody adds.

     The button is appended inside that anchor: .os2-card__img is already
     position:relative, and for other containers we promote the anchor. The
     click handler preventDefaults, so the surrounding link never fires. */
  var HANDLE_RE = /\/products\/([^\/?#]+)/;

  function injectQuickView(root) {
    var links = (root || document).querySelectorAll('a[href*="/products/"]:not([data-os2x-qvhost])');
    Array.prototype.forEach.call(links, function (a) {
      a.setAttribute('data-os2x-qvhost', '');

      if (a.closest('[data-os2x-noqv]')) return;
      if (a.querySelector('.os2x-qvbtn')) return;

      var img = a.querySelector('img');
      if (!img) return;

      // Skip inline thumbnails (search rows, mega-menu promo, cart lines) —
      // a quick view pill does not fit and is not wanted there.
      if (a.offsetWidth && a.offsetWidth < 120) return;

      var m = HANDLE_RE.exec(a.getAttribute('href') || '');
      if (!m) return;

      if (getComputedStyle(a).position === 'static') a.style.position = 'relative';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'os2x-qvbtn';
      btn.setAttribute('data-os2x-quickview', m[1]);
      btn.setAttribute('aria-label', 'Quick view ' + (img.getAttribute('alt') || 'product'));
      btn.innerHTML = EYE + '<span>Quick view</span>';
      a.appendChild(btn);
    });
  }

  var overlay = null;
  var cache = {};
  var state = null;

  function buildOverlay() {
    if (overlay) return overlay;
    overlay = document.createElement('div');
    overlay.className = 'os2x-qv';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Quick view');
    overlay.innerHTML =
      '<div class="os2x-qv__scrim" data-os2x-dismiss></div>' +
      '<div class="os2x-qv__card">' +
        '<button type="button" class="os2x-qv__close" data-os2x-dismiss aria-label="Close quick view">&times;</button>' +
        '<div class="os2x-qv__media">' +
          '<div class="os2x-qv__stage" data-os2x-stage></div>' +
          '<div class="os2x-qv__thumbs" data-os2x-thumbs></div>' +
        '</div>' +
        '<div class="os2x-qv__body" data-os2x-body>' +
          '<div class="os2x-qv__skel"><span></span><span></span><span></span><span></span></div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);
    return overlay;
  }

  function open() {
    buildOverlay();
    overlay.setAttribute('data-open', '');
    overlay.classList.remove('is-out');
    document.documentElement.style.overflow = 'hidden';
    requestAnimationFrame(function () { overlay.classList.add('is-in'); });
  }

  function close() {
    if (!overlay || !overlay.hasAttribute('data-open')) return;
    overlay.classList.remove('is-in');
    overlay.classList.add('is-out');
    document.documentElement.style.overflow = '';
    var done = function () {
      overlay.removeAttribute('data-open');
      overlay.classList.remove('is-out');
      state = null;
    };
    if (REDUCED) { done(); return; }
    setTimeout(done, 300);
  }

  /* --------------------------------------------- 3. QUICK VIEW: rendering */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var SWATCHES = [
    ['assort', 'linear-gradient(135deg,#16181C 0 33%,#4A3527 33% 66%,#C6C8CC 66%)'],
    ['mixed', 'linear-gradient(135deg,#16181C 0 33%,#4A3527 33% 66%,#C6C8CC 66%)'],
    ['gunmetal', '#55585E'], ['black', '#16181C'],
    ['tortoise', '#6B4423'], ['demi', '#6B4423'],
    ['brown', '#4A3527'], ['green', '#24422F'], ['olive', '#4A4B2A'],
    ['navy', '#16233D'], ['blue', '#1E3A5F'], ['sky', '#7FA6C9'],
    ['silver', '#C6C8CC'], ['gold', '#B8934A'], ['champagne', '#B8934A'],
    ['bronze', '#7A5230'], ['copper', '#8C5A3C'], ['beige', '#D8C9AE'],
    ['khaki', '#9A9067'], ['grey', '#7C7F86'], ['gray', '#7C7F86'],
    ['transparent', '#DAD8D2'], ['crystal', '#DAD8D2'], ['clear', '#DAD8D2'],
    ['pink', '#C98A93'], ['rose', '#C98A93'], ['yellow', '#D8B84A'],
    ['orange', '#C4703A'], ['wine', '#5E2233'],
    ['red', '#8C3A3A'], ['maroon', '#8C3A3A'],
    ['purple', '#5B4A7A'], ['violet', '#5B4A7A'], ['white', '#F2F2F0'],
    /* Generic light/dark finishes are last so a name like "Dark Tortoise"
       still matches tortoise first. */
    ['dark', '#2B2E34'], ['light', '#DCDDE0']
  ];

  function swatchFor(name) {
    var n = String(name || '').toLowerCase();
    for (var i = 0; i < SWATCHES.length; i++) {
      if (n.indexOf(SWATCHES[i][0]) > -1) return SWATCHES[i][1];
    }
    return '';
  }

  function variantImage(product, variant) {
    if (variant && variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
    return product.featured_image || (product.images && product.images[0]) || '';
  }

  function sized(url, w) {
    if (!url) return '';
    var clean = url.split('?')[0];
    return clean.replace(/(\.(?:png|jpe?g|webp|gif))$/i, '_' + w + 'x$1') + '?v=1';
  }

  /* MOQ rule mirrors snippets/os2-card.liquid: sample kits and pre-made
     assorted trays are exempt and ship as one line at qty 1. Pack-size products
     (tag quantity_variants, e.g. OS26 Spring Reading 3/6/12 Pcs) are exempt too,
     same as the product page: every pack already holds 3+ pieces. 2026-10-13. */
  function moqFor(product, variant) {
    var type = (product.type || '').toLowerCase();
    var tags = (product.tags || []).join(' ').toLowerCase();
    if (type === 'sample kit' || tags.indexOf('sample_kit') > -1) return 1;
    if (tags.indexOf('sub_premium_display_trays') > -1) return 1;
    if (tags.indexOf('quantity_variants') > -1) return 1;
    if (variant && /assort/i.test(variant.title || '')) return 1;
    return MOQ;
  }

  function variantById(id) {
    if (!state || !state.product) return null;
    var list = state.product.variants || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === id) return list[i];
    }
    return null;
  }

  /* Sum the per-colour rows, and keep everything that depends on that total in
     step: the header count, the running value, the MOQ line and whether Add is
     allowed. Called on every stepper tap and every typed digit, so it reads the
     inputs rather than keeping a parallel tally that could drift from them. */
  function recount() {
    if (!overlay) return;
    var rows = overlay.querySelectorAll('[data-os2x-bulkrow]');
    if (!rows.length) return;

    var total = 0;
    var paise = 0;

    Array.prototype.forEach.call(rows, function (r) {
      var input = r.querySelector('[data-os2x-bulkqty]');
      if (!input) return;
      var q = parseInt(input.value, 10);
      if (!(q > 0)) q = 0;
      /* Normalise what the dealer typed — "-2" and "abc" both mean zero. */
      if (String(q) !== input.value) input.value = q;
      r.classList.toggle('is-on', q > 0);
      total += q;
      var v = variantById(parseInt(r.getAttribute('data-vid'), 10));
      if (v) paise += (v.price || 0) * q;
    });

    var min = state ? moqFor(state.product, state.variant) : MOQ;

    var head = overlay.querySelector('[data-os2x-bulktotal]');
    if (head) {
      head.textContent = total + (total === 1 ? ' pc' : ' pcs') +
        (CFG.gated || !paise ? '' : ' · ' + money(paise));
    }

    var note = overlay.querySelector('[data-os2x-moqnote]');
    if (note) {
      var ok = total >= min;
      note.classList.toggle('is-ok', ok);
      note.innerHTML = ok
        ? '<b>Minimum met</b> — ' + total + ' pcs across the colours you picked.'
        : 'Add <b>' + (min - total) + ' more</b> to reach the ' + min +
          '-piece minimum. Colours of the same model count together.';
    }

    var add = overlay.querySelector('[data-os2x-add]');
    if (add && !CFG.gated) add.disabled = total < min;
  }

  function render(product) {
    var body = overlay.querySelector('[data-os2x-body]');
    var stage = overlay.querySelector('[data-os2x-stage]');
    var thumbs = overlay.querySelector('[data-os2x-thumbs]');

    var variants = (product.variants || []).filter(function (v) { return v; });
    var current = variants.filter(function (v) { return v.available; })[0] || variants[0];
    if (!current) return;

    state = { product: product, variant: current };

    function paintStage(url) {
      stage.classList.add('is-swapping');
      var img = new Image();
      img.alt = product.title;
      img.src = sized(url, 900);
      img.onload = function () {
        stage.innerHTML = '';
        stage.appendChild(img);
        requestAnimationFrame(function () { stage.classList.remove('is-swapping'); });
      };
      img.onerror = function () { stage.classList.remove('is-swapping'); };
    }
    paintStage(variantImage(product, current));

    var images = (product.images || []).slice(0, 8);
    if (images.length > 1) {
      thumbs.innerHTML = images.map(function (src, i) {
        return '<button type="button" data-os2x-img="' + esc(src) + '" aria-current="' + (i === 0) + '">' +
          '<img src="' + esc(sized(src, 120)) + '" alt="" loading="lazy"></button>';
      }).join('');
    } else {
      thumbs.innerHTML = '';
    }

    var optionNames = product.options || [];
    if (optionNames.length && typeof optionNames[0] === 'object') {
      optionNames = optionNames.map(function (o) { return o.name; });
    }

    var multi = variants.length > 1;

    /* PER-COLOUR QUANTITIES, not a colour picker plus one quantity box.

       The old modal let a dealer choose one variant and one number, which is
       not how this trade buys: an order is "four black, two brown, six grey"
       against a minimum that counts the colours together. That is exactly what
       .osr-bulk does on the product page, so this mirrors it — same row shape,
       same stepper, same MOQ arithmetic — rather than teaching a second
       control for the same job.

       The row's name and swatch stay a real <button>: tapping it previews that
       colour on the stage, which is what the swatch strip used to be for. So
       the strip is gone rather than duplicated — one row per colour, doing both
       jobs. Only the stepper is separate, and the click handler checks it
       first so + and − never also fire the preview. */
    var optionsHtml = '';
    if (multi) {
      optionsHtml =
        '<div class="os2x-qv__bulk" data-os2x-bulk>' +
          '<div class="os2x-qv__bulkhead">' +
            '<span>' + esc(optionNames[0] || 'Colour') + ' · ' + variants.length + '</span>' +
            '<b data-os2x-bulktotal>0 pcs</b>' +
          '</div>' +
          '<div class="os2x-qv__rows">' +
          variants.map(function (v) {
            var img = v.featured_image && v.featured_image.src ? sized(v.featured_image.src, 90) : '';
            var dot = img
              ? 'background-image:url(' + esc(img) + ');background-size:cover'
              : 'background:' + (swatchFor(v.title) || '#C3C3C9');
            var meta = v.sku ? esc(v.sku) : '';
            if (!v.available) meta = meta ? meta + ' · out of stock' : 'out of stock';
            return '<div class="os2x-qv__row' + (v.available ? '' : ' is-out') + '"' +
              ' data-os2x-bulkrow data-vid="' + v.id + '">' +
              '<button type="button" class="os2x-qv__rowpick" data-os2x-variant="' + v.id + '"' +
                ' aria-pressed="' + (v.id === current.id) + '"' +
                ' aria-label="Show ' + esc(v.title) + '">' +
                '<span class="os2x-qv__dot" style="' + dot + '" aria-hidden="true"></span>' +
                '<span class="os2x-qv__rowname"><b>' + esc(v.title) + '</b><i>' + meta + '</i></span>' +
              '</button>' +
              (v.available
                ? '<span class="os2x-qv__step">' +
                    '<button type="button" data-os2x-bulkstep="-1" aria-label="Decrease ' + esc(v.title) + '">−</button>' +
                    '<input type="number" min="0" value="0" inputmode="numeric" data-os2x-bulkqty' +
                      ' aria-label="Quantity for ' + esc(v.title) + '">' +
                    '<button type="button" data-os2x-bulkstep="1" aria-label="Increase ' + esc(v.title) + '">+</button>' +
                  '</span>'
                : '<span class="os2x-qv__outtag">—</span>') +
            '</div>';
          }).join('') +
          '</div>' +
        '</div>';
    }

    var chips = [];
    if (product.type) chips.push(product.type);
    var series = (product.tags || []).filter(function (t) { return t.indexOf('series_') === 0; })[0];
    if (series) chips.push(series.replace('series_', '').replace(/_/g, ' ').toUpperCase());
    if (current.sku) chips.push(current.sku);

    var gated = !!CFG.gated;
    var moq = moqFor(product, current);

    var priceHtml = gated
      ? '<div class="os2x-qv__price" data-os2x-price>Login to view price</div>'
      : '<div class="os2x-qv__price" data-os2x-price>' + money(current.price) +
        '<small>per piece · GST incl.</small></div>';

    var actionHtml;
    if (gated) {
      actionHtml =
        '<div class="os2x-qv__row">' +
          '<a class="os2x-qv__cta" style="display:grid;place-items:center;text-decoration:none" href="' +
          esc(CFG.loginUrl || '/account/login') + '">Login to order</a>' +
        '</div>' +
        '<p class="os2x-qv__note">Prices and ordering unlock once your dealer account is approved.</p>';
    } else if (multi) {
      /* No quantity box beside the button: the quantities are the rows above,
         and a second number here would be one more thing to reconcile. Add
         starts disabled and the MOQ line says how many pieces short it is. */
      actionHtml =
        '<div class="os2x-qv__row">' +
          '<button type="button" class="os2x-qv__cta" data-os2x-add disabled>Add to order</button>' +
        '</div>' +
        '<p class="os2x-qv__moq" data-os2x-moqnote></p>';
    } else {
      actionHtml =
        '<div class="os2x-qv__row">' +
          '<div class="os2x-qv__step">' +
            '<button type="button" data-os2x-qty="-1" aria-label="Decrease quantity">−</button>' +
            '<input type="number" data-os2x-qtyinput value="' + moq + '" min="1" inputmode="numeric" aria-label="Quantity">' +
            '<button type="button" data-os2x-qty="1" aria-label="Increase quantity">+</button>' +
          '</div>' +
          '<button type="button" class="os2x-qv__cta" data-os2x-add' +
          (current.available ? '' : ' disabled') + '>' +
          (current.available ? 'Add to order' : 'Out of stock') + '</button>' +
        '</div>' +
        '<p class="os2x-qv__note" data-os2x-moqnote>' +
          (moq > 1
            ? 'Minimum ' + moq + ' pieces per product.'
            : 'This item is exempt from the ' + MOQ + '-piece minimum.') +
        '</p>';
    }

    body.innerHTML =
      '<div class="os2x-qv__eyebrow">Quick view</div>' +
      '<h2 class="os2x-qv__title"><a href="' + esc(product.url) + '">' + esc(product.title) + '</a></h2>' +
      (chips.length
        ? '<div class="os2x-qv__meta">' + chips.map(function (c) {
            return '<span class="os2x-qv__tag">' + esc(c) + '</span>';
          }).join('') + '</div>'
        : '') +
      priceHtml +
      optionsHtml +
      actionHtml +
      '<a class="os2x-qv__link" href="' + esc(product.url) + '">Full product details &rarr;</a>';

    /* Paint the MOQ line and the disabled Add from a zeroed grid. */
    recount();
  }

  function selectVariant(id) {
    if (!state) return;
    var v = variantById(id);
    if (!v || !v.available) return;
    state.variant = v;

    overlay.querySelectorAll('[data-os2x-variant]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(parseInt(b.getAttribute('data-os2x-variant'), 10) === id));
    });
    var nameEl = overlay.querySelector('[data-os2x-optname]');
    if (nameEl) nameEl.textContent = v.title;

    var priceEl = overlay.querySelector('[data-os2x-price]');
    if (priceEl && !CFG.gated) {
      priceEl.innerHTML = money(v.price) + '<small>per piece · GST incl.</small>';
    }

    var addBtn = overlay.querySelector('[data-os2x-add]');
    /* Only the single-variant modal ties Add to the selected colour; with the
       quantity grid, Add answers to the total and recount() owns it. */
    if (addBtn && !overlay.querySelector('[data-os2x-bulkrow]')) {
      addBtn.disabled = !v.available;
      addBtn.textContent = v.available ? 'Add to order' : 'Out of stock';
    }

    if (v.featured_image && v.featured_image.src) {
      var stage = overlay.querySelector('[data-os2x-stage]');
      var img = new Image();
      img.alt = state.product.title;
      img.src = sized(v.featured_image.src, 900);
      stage.classList.add('is-swapping');
      img.onload = function () {
        stage.innerHTML = '';
        stage.appendChild(img);
        requestAnimationFrame(function () { stage.classList.remove('is-swapping'); });
      };
    }
  }

  function load(handle) {
    open();
    if (cache[handle]) { render(cache[handle]); return; }
    var body = overlay.querySelector('[data-os2x-body]');
    body.innerHTML = '<div class="os2x-qv__skel"><span></span><span></span><span></span><span></span></div>';
    fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (p) { cache[handle] = p; render(p); })
      .catch(function () {
        body.innerHTML = '<p class="os2x-qv__note">Could not load this product. ' +
          '<a class="os2x-qv__link" href="/products/' + esc(handle) + '">Open the product page</a></p>';
      });
  }

  /* ------------------------------------------------------- 4. DELEGATION */

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t.closest) return;

    var trigger = t.closest('[data-os2x-quickview]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      load(trigger.getAttribute('data-os2x-quickview'));
      return;
    }

    if (!overlay || !overlay.hasAttribute('data-open')) return;

    if (t.closest('[data-os2x-dismiss]')) { e.preventDefault(); close(); return; }

    /* Before the variant check: the steppers sit inside a row that also carries
       a preview button, and + must not double as "show me this colour". */
    var bstep = t.closest('[data-os2x-bulkstep]');
    if (bstep) {
      e.preventDefault();
      var row = bstep.closest('[data-os2x-bulkrow]');
      var qin = row && row.querySelector('[data-os2x-bulkqty]');
      if (!qin) return;
      var next = (parseInt(qin.value, 10) || 0) + parseInt(bstep.getAttribute('data-os2x-bulkstep'), 10);
      qin.value = Math.max(0, next);
      recount();
      return;
    }

    var vbtn = t.closest('[data-os2x-variant]');
    if (vbtn) { e.preventDefault(); selectVariant(parseInt(vbtn.getAttribute('data-os2x-variant'), 10)); return; }

    var thumb = t.closest('[data-os2x-img]');
    if (thumb) {
      e.preventDefault();
      overlay.querySelectorAll('[data-os2x-img]').forEach(function (b) {
        b.setAttribute('aria-current', String(b === thumb));
      });
      var stage = overlay.querySelector('[data-os2x-stage]');
      var img = new Image();
      img.src = sized(thumb.getAttribute('data-os2x-img'), 900);
      img.alt = state && state.product ? state.product.title : '';
      stage.classList.add('is-swapping');
      img.onload = function () {
        stage.innerHTML = '';
        stage.appendChild(img);
        requestAnimationFrame(function () { stage.classList.remove('is-swapping'); });
      };
      return;
    }

    var step = t.closest('[data-os2x-qty]');
    if (step) {
      e.preventDefault();
      var input = overlay.querySelector('[data-os2x-qtyinput]');
      if (!input) return;
      var min = state ? moqFor(state.product, state.variant) : 1;
      var nextQty = (parseInt(input.value, 10) || min) + parseInt(step.getAttribute('data-os2x-qty'), 10);
      input.value = Math.max(min, nextQty);
      return;
    }

    var add = t.closest('[data-os2x-add]');
    if (add) {
      e.preventDefault();
      if (!state || !state.variant) return;
      if (CFG.gated) {
        if (window.os2Toast) {
          window.os2Toast(CFG.loggedIn ? 'Your dealer account is awaiting approval' : 'Login to order',
            CFG.loginUrl, 'Log in');
        }
        return;
      }
      if (typeof window.os2Add !== 'function') return;

      var rows = overlay.querySelectorAll('[data-os2x-bulkrow]');
      var lines = [];
      var qty = 0;
      var min2 = moqFor(state.product, state.variant);

      if (rows.length) {
        Array.prototype.forEach.call(rows, function (r) {
          var qi = r.querySelector('[data-os2x-bulkqty]');
          var q = parseInt(qi && qi.value, 10) || 0;
          if (q < 1) return;
          lines.push({ id: parseInt(r.getAttribute('data-vid'), 10), quantity: q });
          qty += q;
        });
        /* recount() keeps Add disabled below the minimum; this is the guard for
           a click that gets through anyway (keyboard, stale DOM). */
        if (!lines.length || qty < min2) { recount(); return; }
      } else {
        var input2 = overlay.querySelector('[data-os2x-qtyinput]');
        qty = Math.max(min2, parseInt(input2 && input2.value, 10) || min2);
        lines = [{ id: state.variant.id, quantity: qty }];
      }

      add.classList.add('is-busy');
      add.textContent = 'Adding…';
      var label = state.product.title +
        (lines.length > 1 ? ' · ' + lines.length + ' colours' : ' · ' + state.variant.title) +
        ' · ' + qty + ' pcs added';
      window.os2Add(lines, label);
      setTimeout(function () { close(); }, 420);
      return;
    }
  });

  /* Typed quantities, not just tapped ones. */
  document.addEventListener('input', function (e) {
    if (!overlay || !overlay.hasAttribute('data-open')) return;
    if (!e.target.closest || !e.target.closest('[data-os2x-bulkqty]')) return;
    recount();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay && overlay.hasAttribute('data-open')) close();
  });

  /* Pointer tilt on the media stage — the "3D" of the pop-up. Pointer coords
     drive rotateX/rotateY; leaving resets. Skipped for coarse pointers and
     under reduced motion. */
  if (!REDUCED && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.addEventListener('pointermove', function (e) {
      if (!overlay || !overlay.hasAttribute('data-open')) return;
      var media = e.target.closest && e.target.closest('.os2x-qv__media');
      var stage = overlay.querySelector('[data-os2x-stage]');
      if (!stage) return;
      if (!media) { stage.style.transform = ''; return; }
      var r = media.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width - .5;
      var py = (e.clientY - r.top) / r.height - .5;
      stage.style.transform = 'rotateY(' + (px * 16).toFixed(2) + 'deg) rotateX(' +
        (-py * 12).toFixed(2) + 'deg)';
    });
    document.addEventListener('pointerleave', function () {
      if (!overlay) return;
      var stage = overlay.querySelector('[data-os2x-stage]');
      if (stage) stage.style.transform = '';
    }, true);
  }

  /* ---------------------------------------------------------- 5. TOGGLES */

  function applyToggle(group, mode) {
    var target = document.querySelector(group.getAttribute('data-os2x-toggle'));
    if (!target) return;
    target.classList.remove('is-compact', 'is-roomy');
    if (mode !== 'default') target.classList.add('is-' + mode);
    group.querySelectorAll('[data-os2x-mode]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-os2x-mode') === mode));
    });
    try { localStorage.setItem('os2x:' + group.getAttribute('data-os2x-toggle'), mode); } catch (err) { /* private mode */ }
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-os2x-mode]');
    if (!b) return;
    var group = b.closest('[data-os2x-toggle]');
    if (!group) return;
    e.preventDefault();
    applyToggle(group, b.getAttribute('data-os2x-mode'));
  });

  function restoreToggles(root) {
    (root || document).querySelectorAll('[data-os2x-toggle]').forEach(function (g) {
      if (g.hasAttribute('data-os2x-ready')) return;
      g.setAttribute('data-os2x-ready', '');
      var saved = null;
      try { saved = localStorage.getItem('os2x:' + g.getAttribute('data-os2x-toggle')); } catch (err) { /* private mode */ }
      if (saved) applyToggle(g, saved);
    });
  }

  /* ------------------------------------------------------------- 6. BOOT */

  function boot(root) {
    enrol(root);
    restoreToggles(root);
    injectQuickView(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* Sections re-render after load; re-scan, debounced. Settles after one extra
     pass because everything we touch is marked and skipped next time. */
  var mo = new MutationObserver(function () {
    clearTimeout(mo._t);
    mo._t = setTimeout(function () { boot(document); }, 120);
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  window.os2QuickView = load;
})();
